import {
  AppPage,
  MimaButton,
  MimaTable,
  MimaNewFilter,
  BusinessInsightComponent as BIC,
  PageLoader,
  MimaText,
} from "../../components";
import styles from "../../assets/styles/SplitPayment/SplitPayment.module.scss";
import { amountFormatter } from "../../utils/utils";
import React, { useMemo, useState } from "react";
import constant from "../../utils/constant";
import moment from "moment";
import { splitpaymentStore } from "../../stores";
import { downloadXlsx2 } from "../../utils/download";

const SplitPayments = ({
  loading,
  refresh,
  isLoading,
  data,
  isFetching,
  limit,
  onLimitChange,
  currentPage,
  setCurrentPage,
  setSearchQuery,
  filterHandler,
}) => {
  const [showFilter, setShowFilter] = useState(false);

  const { splitAnalysis } = splitpaymentStore;

  const filterByData = [{ value: "Date", key: "createdAt" }];

  const tableData = useMemo(() => {
    if (data?.status === constant.Success) {
      return data?.data?.value || [];
    }
    return [];
  }, [data]);

  const tableColumns = [
    {
      Header: "Date",
      accessor: "createdAt",
      Cell: ({ cell }) => {
        const date = cell.row.original.createdAt;
        const formattedDate = moment(date).format("DD/MM/YYYY hh:mm a");
        return <span>{formattedDate}</span>;
      },
    },
    {
      Header: "Business Name",
      accessor: "tradingName ",
      Cell: ({ cell }) => {
        const bizName = cell.row.original.business.tradingName;
        return <span>{bizName}</span>;
      },
    },
    {
      Header: "Description",
      accessor: "narration",
    },
    {
      Header: "Customer",
      accessor: "fullname ",
      Cell: ({ cell }) => {
        const customerName =
          cell.row.original.order?.invoice?.customer?.fullname;
        return <span>{customerName}</span>;
      },
    },
    {
      Header: "Beneficiary Name",
      accessor: "beneficiaryAccountName",
    },
    {
      Header: "Total Amount",
      accessor: "totalAmount",
      Cell: ({ cell }) => {
        const totalAmount = cell.row.original.totalAmount;
        const currencyCode = cell.row.original.currencyCode;
        const formattedAmount =
          amountFormatter(currencyCode).format(totalAmount);
        return <span>{formattedAmount}</span>;
      },
    },
    {
      Header: "Split Amount",
      accessor: "transactionAmount",
      Cell: ({ cell }) => {
        const transactionAmount = cell.row.original.transactionAmount;
        const currencyCode = cell.row.original.currencyCode;
        const formattedAmount =
          amountFormatter(currencyCode).format(transactionAmount);
        return <span>{formattedAmount}</span>;
      },
    },
    {
      Header: "Deduction",
      accessor: "deduction",
      Cell: ({ cell }) => {
        const deduction = cell.row.original.payDeductions;
        const formattedAmount = amountFormatter().format(
          deduction.reduce((partialSum, a) => partialSum + a.amount, 0) || 0
        );

        return <span>{formattedAmount}</span>;
      },
    },
    {
      Header: "Addition",
      accessor: "addition",
      Cell: ({ cell }) => {
        const payAdditions = cell.row.original.payAdditions;
        const formattedAmount = amountFormatter().format(
          payAdditions.reduce((partialSum, a) => partialSum + a.amount, 0) || 0
        );
        return <span>{formattedAmount}</span>;
      },
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ cell }) => {
        const status = cell.value;
        const statusClass = `status ${status === "PAID" ? "status__green" : status === "VOID" ? "status__grey" : "status__dark__yellow"}`;
        return <span className={statusClass}>{status}</span>;
      },
    },
  ];

  const printData = tableData.map((item) => ({
    Date: item?.createdAt
      ? moment(item?.createdAt).format("DD-MM-YYYY HH:mm a")
      : "",
    "Business Name": item?.business?.tradingName || "",
    Description: item?.narration || "",
    Customer: item?.order?.invoice?.customer?.fullname || "",
    "Beneficiary Name": item.beneficiaryAccountName || "",
    "Total Amount":
      amountFormatter(item.currencyCode).format(item.totalAmount) || "",
    "Split Amount":
      amountFormatter(item.currencyCode).format(item.transactionAmount) || "",
    Deduction: amountFormatter().format(
      item.payDeductions.reduce((partialSum, a) => partialSum + a.amount, 0) ||
        0
    ),
    Addition: amountFormatter().format(
      item.payAdditions.reduce((partialSum, a) => partialSum + a.amount, 0) || 0
    ),
    Status: item.status,
  }));

  return (
    <AppPage pageTitle={`Split Payments`} refresh={refresh}>
      {loading ? (
        <PageLoader title="While we set things up for you" loading={loading} />
      ) : (
        <>
          <div className={styles.main__metrics}>
            <BIC
              title="Total Sales"
              amount={amountFormatter().format(splitAnalysis?.totalAmount || 0)}
              styleClass={styles.main__metrics_lightBlue2}
            />
            <BIC
              title="Split Disbursement Amount"
              amount={amountFormatter().format(
                splitAnalysis?.totalSplitAmount || 0
              )}
              styleClass={styles.main__metrics_yellow}
            />
            <BIC
              title="Split Payment Profit Margin"
              amount={amountFormatter().format(splitAnalysis.totalProfit || 0)}
              styleClass={styles.main__metrics_green}
            />

            <BIC
              title="Total Split transactions"
              amount={splitAnalysis.totalCount || 0}
              styleClass={styles.main__metrics_lightBlue}
            />
          </div>
          {splitAnalysis?.summaries?.length > 0 && (
            <>
              <MimaText mt={5} mb={3} variant="bodyBold" align="center">
                Split Payment Analysis
              </MimaText>
              <div className={styles.threeCards}>
                {splitAnalysis?.summaries?.map((analysis, i) => (
                  <div key={i} className={styles.singleCard}>
                    <MimaText variant="subtitleBold">
                      {" "}
                      {analysis._id.status}{" "}
                    </MimaText>
                    <MimaText labelTitle="Total Amount">
                      {amountFormatter().format(analysis.totalAmount)}
                    </MimaText>
                    <MimaText labelTitle="Split Amount">
                      {amountFormatter().format(analysis.splitTotal)}
                    </MimaText>
                    <MimaText labelTitle="Count">
                      {analysis.totalCount}
                    </MimaText>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
      <>
        <div className={styles.filterClass}>
          <MimaButton
            title={showFilter ? "Close Filter" : "Filter"}
            onClick={() => setShowFilter(!showFilter)}
          />

          <MimaButton
            title={`Download Report`}
            width={27}
            onClick={() => {
              downloadXlsx2(printData);
            }}
          />
        </div>

        <MimaNewFilter
          showFilter={showFilter}
          setShowFilter={setShowFilter}
          filterByData={filterByData}
          filterHandler={filterHandler}
        />

        <MimaTable
          tableData={tableData}
          tableColumns={tableColumns}
          searchPlaceholder="Search Subscription"
          searchVariant="wide"
          totalItems={data?.data?.totalCounts}
          onLimitChange={onLimitChange}
          isLoading={isLoading}
          isFetching={isFetching}
          limit={limit}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          setSearchQuery={setSearchQuery}
        />
      </>
    </AppPage>
  );
};

export default SplitPayments;
